import { useEffect, useState } from "react";

import { useAuthSession } from "@/lib/session-context";
import type { PricingUnit } from "@/types/generated/PricingUnit";

const STORAGE_KEY = "app.pricing-unit";
const EVENT_NAME = "app:pricing-unit";

/** Every unit the preference accepts, in display order. */
export const PRICING_UNITS: readonly PricingUnit[] = [
  "dollars_per_kwh",
  "cents_per_kwh",
  "dollars_per_mwh",
];

/**
 * The signed-in user's own electric rate display unit (AB#8329).
 *
 * The brokerage's super-admin sets a system default; this preference
 * shadows it for one user on one browser. Persisted to `localStorage`
 * and fanned out the same way guidance visibility and the theme are, so
 * picking a unit takes effect with no Save button. `null` in storage
 * means the user never chose, and the system default stands.
 *
 * Pass the session's `default_pricing_unit` as `systemDefault`.
 */
export function usePricingUnit(
  systemDefault: PricingUnit,
): [PricingUnit, (unit: PricingUnit) => void] {
  const [override, setOverride] = useState<PricingUnit | null>(() =>
    readStored(),
  );

  useEffect(() => {
    const onCustom = (e: Event) => {
      setOverride((e as CustomEvent<PricingUnit>).detail);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setOverride(parse(e.newValue));
    };
    window.addEventListener(EVENT_NAME, onCustom as EventListener);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onCustom as EventListener);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const update = (unit: PricingUnit) => {
    setOverride(unit);
    try {
      localStorage.setItem(STORAGE_KEY, unit);
    } catch {
      // localStorage may throw in private mode / disabled storage — the
      // in-memory state still works for the current page.
    }
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: unit }));
  };

  return [override ?? systemDefault, update];
}

/**
 * The effective unit for a component rendered inside the app shell.
 *
 * Reach for this instead of `getActivePricingUnit()` / `pricingUnitLabel()`
 * wherever a component's OUTPUT depends on the unit. Those two read a
 * module-level value and so create no subscription — the component keeps its
 * old label until something else re-renders it, which is exactly what left the
 * proposal's rate labels stale after a change in Preferences.
 *
 * Throws outside the shell (no session), same as `useAuthSession`.
 */
export function useEffectivePricingUnit(): PricingUnit {
  const session = useAuthSession();
  const [unit] = usePricingUnit(session.default_pricing_unit);
  return unit;
}

function readStored(): PricingUnit | null {
  try {
    return parse(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

/** Anything not in the catalog reads as "never chose" rather than
 *  poisoning the formatter with a stale or hand-edited value. */
function parse(raw: string | null): PricingUnit | null {
  return PRICING_UNITS.includes(raw as PricingUnit)
    ? (raw as PricingUnit)
    : null;
}
